import {
    Component,
    computed,
    effect,
    inject,
    Injector,
    input,
    OnInit,
    output,
    signal,
    untracked,
} from '@angular/core';
import {
    UserRole,
    ViewConfig,
    ViewType,
} from '../../models/view-config.interface';
import { ViewConfigurationService } from '../../services/view-config.service';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { PageEvent } from '@angular/material/paginator';
import {
    SelectionEvent,
    SortEvent,
} from '../../models/generic-table.interface';
import { MatDialog } from '@angular/material/dialog';
import { ToastService } from '@app/core/services/toast.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { QuotasService } from '@app/core/services/quotas.service';
import { SelectDisplayedColsDialogComponent } from '@app/shared/components/select-displayed-cols-dialog/select-displayed-cols-dialog.component';
import { UntilDestroy } from '@ngneat/until-destroy';
import { lastValueFrom } from 'rxjs';
import { FilterUserSitesComponent } from '@app/shared/components/filter-user-sites/filter-user-sites.component';
import { EventsPaginatedService } from '@app/shared/services/events-paginated.service';
import {
    SearchStore,
} from '@app/stores/searchStore';
import {
    AppStore,
} from '@app/stores/appStore';
import { TablePreferencesStore } from '@app/stores/tablePreferencesStore';
import {
    DeliveryNotesStore,
} from '@app/stores/deliveryNotesStore';
import {
    FoldersStore,
} from '@app/stores/foldersStore';
import {
    ToursStore,
} from '@app/stores/toursStore';
import { EventsStore } from '@app/stores/eventsStore';
import {
    SelectionStore,
    SelectableItem,
} from '@app/stores/selectionStore';
import { LoginOrganizationSite, UserRoles } from 'api/models';
import { PaginatedTableFilterComponent } from '../table/paginated-table-filter/paginated-table-filter.component';
import { GenericTableComponent } from '../generic-table/generic-table.component';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-configurable-view',
    templateUrl: './configurable-view.component.html',
    styleUrls: ['./configurable-view.component.scss'],
    imports: [
        PaginatedTableFilterComponent,
        GenericTableComponent,
        TranslateModule,
    ],
})
export class ConfigurableViewComponent<
    T extends SelectableItem = SelectableItem,
> implements OnInit {
    userRole = input<UserRole>('consignor');
    initialViewType = input<ViewType>('folders');
    contextData = input<any>({});

    viewChanged = output<ViewType>();
    dataLoaded = output<T[]>();
    actionTriggered = output<{
        actionId: string;
        data: any;
    }>();

    private appStore = inject(AppStore);
    private tablePreferencesStore = inject(TablePreferencesStore);
    private searchStore = inject(SearchStore);
    private foldersStore = inject(FoldersStore);
    private toursStore = inject(ToursStore);
    private deliveryNotesStore = inject(DeliveryNotesStore);
    private eventsStore = inject(EventsStore);
    private selectionStore = inject(SelectionStore);

    // Signals for reactive state
    currentViewType = this.searchStore.currentViewType; // Use store signal
    currentConfig = signal<ViewConfig | null>(null);
    isTourPlanMode = this.appStore.isTourPlanMode; // Use store signal for tour plan mode
    selectedItems = computed(() => this.selectionStore.selectedItems()); // Use store signal for selected items
    organizationSiteKey = this.appStore.currentOrganizationSiteKey; // Use store signal for organization site key
    data = computed(() => {
        const config = this.currentConfig();

        if (config?.type === 'deliverynotes') {
            return this.deliveryNotesStore.deliveryNotes() as T[];
        } else if (config?.type === 'folders') {
            return this.foldersStore.folders() as T[];
        } else if (config?.type === 'tours') {
            return this.toursStore.tours() as T[];
        } else if (config?.type === 'notifications') {
            return this.eventsPaginatedService.filteredEvents() as T[];
        }
        return [];
    });

    loading = computed(() => {
        const config = this.currentConfig();
        if (config?.type === 'deliverynotes') {
            return this.deliveryNotesStore.loading();
        } else if (config?.type === 'folders') {
            return this.foldersStore.loading();
        } else if (config?.type === 'tours') {
            return this.toursStore.loading();
        } else if (config?.type === 'notifications') {
            return this.eventsStore.loading();
        }
        return false;
    });
    error = computed(() => {
        const config = this.currentConfig();
        if (config?.type === 'deliverynotes') {
            return this.deliveryNotesStore.error();
        } else if (config?.type === 'folders') {
            return this.foldersStore.error();
        } else if (config?.type === 'tours') {
            return this.toursStore.error();
        } else if (config?.type === 'notifications') {
            return this.eventsStore.error();
        }
        return null;
    });

    // Computed values
    toggleConfig = computed(() => {
        return this.viewConfigService.getViewToggleConfig(this.userRole());
    });

    availableViews = computed(() => {
        return this.viewConfigService.getAvailableViews(this.userRole());
    });

    tableColumns = computed(() => {
        const config = this.currentConfig();
        if (!config) return [];

        const allColumns = config.tableConfig.columns;
        const viewType = config.type;
        const userRole = config.userRole;

        // Check if there are custom column visibility settings
        if (
            this.tablePreferencesStore.hasColumnVisibilitySettings(
                this.sessionService.getSession()._key,
                viewType,
                userRole
            )
        ) {
            const visibleColumnKeys = this.tablePreferencesStore.getVisibleColumns(
                this.sessionService.getSession()._key,
                viewType,
                userRole
            );

            // If no columns are selected, show all columns
            if (visibleColumnKeys.length === 0) {
                return allColumns;
            }

            // Filter columns based on visibility settings, but always include actions column
            return allColumns.filter(
                column =>
                    visibleColumnKeys.includes(String(column.key)) ||
                    column.key === 'actions'
            );
        }

        // No custom settings, return all columns
        return allColumns;
    });

    headerActions = computed(() => {
        const config = this.currentConfig();
        return config?.headerActionConfig?.headerActions || [];
    });

    filterConfig = computed(() => {
        const config = this.currentConfig();
        return config ? config.filterConfig : null;
    });

    permissions = computed(() => {
        const config = this.currentConfig();
        return config ? config.permissions : null;
    });

    // Filter button visibility
    shouldShowLocationFilter = computed(() => {
        const config = this.currentConfig();
        // Hide location filter when in tour plan mode
        if (this.appStore.isTourPlanMode()) {
            return false;
        }
        // Show for delivery notes, folders, and tours views when user has view permissions
        // Hide for notifications view since it shows all sites with selfCheckin
        return (
            (config?.type === 'deliverynotes' ||
                config?.type === 'folders' ||
                config?.type === 'tours') &&
            (config?.permissions?.canView ?? true)
        );
    });

    shouldShowColumnFilter = computed(() => {
        const config = this.currentConfig();
        // Show when user has view permissions - default to true
        return config?.permissions?.canView ?? true;
    });

    shouldShowPagination = computed(() => {
        const config = this.currentConfig();
        // Hide pagination for notifications view
        return config?.type !== 'notifications';
    });

    // Location filter state - connected to appStore
    filteredLocationsCount = computed(() => {
        const selectedKeys = this.appStore.getSelectedLocationKeys(
            this.userRole()
        );
        // If no locations are selected, the current location is implicitly selected
        return selectedKeys.length > 0 ? selectedKeys.length : 1;
    });

    filteredColumnsCount = computed(() => {
        const config = this.currentConfig();
        if (!config) return 0;

        const viewType = config.type;
        const userRole = config.userRole;

        // Get all available columns from the config
        const allColumns = config.tableConfig.columns.map(col =>
            String(col.key)
        );
        const currentVisibleColumns = this.tablePreferencesStore.hasColumnVisibilitySettings(
            this.sessionService.getSession()._key,
            viewType,
            userRole
        )
            ? this.tablePreferencesStore.getVisibleColumns(
                  this.sessionService.getSession()._key,
                  viewType,
                  userRole
              )
            : allColumns; // Default to all columns if no settings
        return allColumns.length - currentVisibleColumns.length;
    });

    isLocationFilterActive = computed(() => {
        const selectedKeys = this.appStore.getSelectedLocationKeys(
            this.userRole()
        );
        const availableSites =
            this.sessionService.getSession()?.organizationSites || [];

        if (this.userRole() === 'consignor') {
            const consignorSites = availableSites.filter(
                site =>
                    site.consignor &&
                    (site.roles.includes(UserRoles.Consignor) ||
                        site.roles.includes(UserRoles.RemoteConsignor))
            );
            return selectedKeys.length !== consignorSites.length;
        }

        return selectedKeys.length !== availableSites.length;
    });

    // Selection state from store
    selectionState = computed(() => this.selectionStore);

    // Pagination and sorting
    pageSize = computed(() => {
        const config = this.currentConfig();
        // Set page size to 100 for notifications, 10 for others
        return config?.type === 'notifications' ? 100 : 10;
    });
    currentPage = signal<number>(0);
    totalItems = computed(() => {
        const config = this.currentConfig();
        if (config?.type === 'deliverynotes') {
            return this.deliveryNotesStore.getTotalCount();
        } else if (config?.type === 'folders') {
            return this.foldersStore.foldersCount();
        } else if (config?.type === 'tours') {
            return this.toursStore.toursCount();
        } else if (config?.type === 'notifications') {
            // For notifications, return filtered count from store
            return this.data().length;
        }
        return 0;
    });

    // Check dialog state to prevent table conflicts
    isSharePermissionsDialogOpen = computed(() =>
        this.appStore.isSharePermissionsDialogOpen()
    );
    sortConfig = signal<{ column: string; direction: 'asc' | 'desc' }>({
        column: '',
        direction: 'asc',
    });

    constructor(
        private viewConfigService: ViewConfigurationService,
        private sessionService: SessionService,
        private dialog: MatDialog,
        private toastService: ToastService,
        private translateService: TranslateService,
        private quotasService: QuotasService,
        private injector: Injector,
        private eventsPaginatedService: EventsPaginatedService
    ) {
        // Effect to update config when view changes
        effect(() => {
            const viewType = this.currentViewType();
            const isTourPlanMode = this.isTourPlanMode();

            // Prevent tours view when tour plan mode is active (force bundle data instead of tour data)
            const effectiveViewType =
                isTourPlanMode && viewType === 'tours' ? 'folders' : viewType;

            const config = this.viewConfigService.getViewConfig(
                this.userRole(),
                effectiveViewType
            );
            this.currentConfig.set(config || null);
        });

        // Single effect to handle both config changes and search store changes
        // This prevents double loading when view type changes
        effect(() => {
            const config = this.currentConfig();
            const refreshTrigger = this.searchStore.refreshTrigger();
            const orgSiteKey = this.organizationSiteKey();

            if (config && orgSiteKey) {
                untracked(() => {
                    this.loadViewData();
                });
            }
        });

        // Track previous view type to only clear on actual changes
        let previousViewType: string | null = null;

        // Effect to clear selection when view type changes (if configured)
        effect(() => {
            const viewType = this.currentViewType();
            const config = this.currentConfig();

            // Only clear if view type actually changed (not on initial load)
            if (previousViewType !== null && previousViewType !== viewType) {
                // Clear selection if the config specifies to do so on view type switch
                const clearCondition =
                    config?.selectionConfig?.clearOnViewTypeSwitch;
                const shouldClear =
                    typeof clearCondition === 'function'
                        ? clearCondition()
                        : clearCondition;

                if (shouldClear) {
                    this.selectionStore.clearSelection();
                }
            }

            // Update previous view type
            previousViewType = viewType;
        });
    }

    ngOnInit(): void {
        // Initialize services with sites based on user role
        this.initializeUserSites();

        // Initialize selection context
        this.selectionStore.setContextData({
            viewType: this.initialViewType(),
            userRole: this.userRole(),
            organizationSiteKey: this.organizationSiteKey() || undefined,
        });

        // Clear any previous selection when switching views
        this.selectionStore.clearSelection();

        // Set initial view type in store
        this.searchStore.updateCurrentViewType(this.initialViewType());

        // Load initial data
        this.loadViewData();
    }

    // View switching - now handled by the store and paginated-table-filter
    // This method is no longer needed as view switching is managed by the store

    // Initialize location filtering for all roles
    private initializeUserSites(): void {
        const session = this.sessionService.getSession();
        if (session && session.organizationSites) {
            // Initialize location selection in appStore with current site
            const currentSiteKey = this.appStore.currentOrganizationSiteKey();
            if (currentSiteKey) {
                this.appStore.initializeSelectedLocations(this.userRole(), currentSiteKey);
            }
        }
    }

    // Data loading
    private async loadViewData(): Promise<void> {
        const config = this.currentConfig();
        const orgSiteKey = this.organizationSiteKey();

        if (!config || !orgSiteKey) {
            return;
        }

        try {
            // Just trigger the API call - data will be available from store
            await config.apiConfig.loadData();

            // Emit the data from the store
            const currentData = this.data();
            this.dataLoaded.emit(currentData);
        } catch (error) {
            this.toastService.error(
                this.translateService.instant('GENERAL.ERROR.LOADING_DATA')
            );
        }
    }

    // Action handling consolidated to header actions only

    // Header action handling
    async onHeaderActionClick(actionId: string): Promise<void> {
        const config = this.currentConfig();
        if (!config) {
            return;
        }

        const action = config.headerActionConfig?.headerActions.find(
            a => a.id === actionId
        );
        if (!action) {
            return;
        }

        // Check quota if required
        if (action.quotaCheck) {
            const quotaReached = await this.quotasService.checkQuotas(
                action.quotaCheck,
                true
            );
            if (quotaReached) {
                return;
            }
        }

        // Check if action is disabled
        if (
            action.disabled &&
            action.disabled(undefined, this.selectedItems())
        ) {
            return;
        }

        // Show confirmation if required
        if (action.confirmation) {
            const confirmed = await this.showConfirmation(
                action.confirmation.message,
                action.confirmation.title
            );
            if (!confirmed) {
                return;
            }
        }

        try {
            await action.handler(undefined, this.selectedItems());
            this.actionTriggered.emit({
                actionId,
                data: this.selectedItems(),
            });

            // Clear selection after successful action (like checkin)
            this.selectionStore.clearSelection();
        } catch (error) {
            this.toastService.error(
                this.translateService.instant('GENERAL.ERROR.ACTION_FAILED')
            );
        }
    }

    // Table event handlers
    onSortChange(event: SortEvent): void {
        console.log('Sort change: ', event);
        this.sortConfig.set({
            column: event.key,
            direction: event.direction as 'asc' | 'desc',
        });

        // Apply sorting to data
        this.applySorting();
    }

    onPageChange(event: PageEvent): void {
        this.currentPage.set(event.pageIndex);
        // Note: pageSize is now computed based on view type, not user-changeable

        // Apply pagination to data
        this.applyPagination();
    }

    onSelectionChange(event: SelectionEvent<T>): void {
        // Update selection store
        this.selectionStore.setSelection(event.selected, {
            viewType: this.currentViewType(),
            userRole: this.userRole(),
            organizationSiteKey: this.organizationSiteKey() || undefined,
        });
    }

    onRefresh(): void {
        this.loadViewData();
    }

    onRowExpand(event: { row: T; expanded: boolean }): void {
        // Handle row expansion event
    }

    // Helper methods
    private applySorting(): void {
        const sortConfig = this.sortConfig();
        if (!sortConfig.column) return;

        // Update the sort in the search store and trigger data refresh
        const sortBy = sortConfig.column as any;
        console.log('Applying sort: ', sortBy, sortConfig.direction);
        this.searchStore.updateSort(sortBy, sortConfig.direction);
        this.searchStore.triggerDataRefresh();
    }

    private applyPagination(): void {
        // This would typically be handled by the API, but for client-side pagination:
        const currentData = this.data();
        const pageSize = this.pageSize();
        const currentPage = this.currentPage();

        const startIndex = currentPage * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedData = currentData.slice(startIndex, endIndex);

        // Update display data (this would depend on your table implementation)
    }

    private async showConfirmation(
        message: string,
        title?: string
    ): Promise<boolean> {
        // Implementation depends on your confirmation dialog
        return confirm(message); // Simple confirm for now
    }

    // Utility methods for template
    isActionVisible(actionId: string, item?: T): boolean {
        const config = this.currentConfig();
        if (!config) return false;

        const action = [
            ...(config.headerActionConfig?.headerActions || []),
        ].find(a => a.id === actionId);

        return action
            ? action.visible
                ? action.visible(item, this.selectedItems())
                : true
            : false;
    }

    isActionDisabled(actionId: string, item?: T): boolean {
        const config = this.currentConfig();
        if (!config) return true;

        const action = [
            ...(config.headerActionConfig?.headerActions || []),
        ].find(a => a.id === actionId);

        return action
            ? action.disabled
                ? action.disabled(item, this.selectedItems())
                : false
            : true;
    }

    getActionLabel(actionId: string): string {
        const config = this.currentConfig();
        if (!config) return '';

        const action = [
            ...(config.headerActionConfig?.headerActions || []),
        ].find(a => a.id === actionId);

        return action
            ? this.translateService.instant(action.translationKey)
            : '';
    }

    // Header action utility methods
    isHeaderActionVisible(actionId: string): boolean {
        const config = this.currentConfig();
        if (!config) return false;

        const action = config.headerActionConfig?.headerActions.find(
            a => a.id === actionId
        );
        return action
            ? action.visible
                ? action.visible(undefined, this.selectedItems())
                : true
            : false;
    }

    isHeaderActionDisabled(actionId: string): boolean {
        const config = this.currentConfig();
        if (!config) return true;

        const action = config.headerActionConfig?.headerActions.find(
            a => a.id === actionId
        );
        return action
            ? action.disabled
                ? action.disabled(undefined, this.selectedItems())
                : false
            : true;
    }

    getHeaderActionLabel(actionId: string): string {
        const config = this.currentConfig();
        if (!config) return '';

        const action = config.headerActionConfig?.headerActions.find(
            a => a.id === actionId
        );
        return action
            ? this.translateService.instant(action.translationKey)
            : '';
    }

    getHeaderActionIcon(actionId: string): string {
        const config = this.currentConfig();
        if (!config) return '';

        const action = config.headerActionConfig?.headerActions.find(
            a => a.id === actionId
        );
        return action ? action.icon || action.svgIcon || '' : '';
    }

    getActionIcon(actionId: string): string {
        const config = this.currentConfig();
        if (!config) return '';

        const action = [
            ...(config.headerActionConfig?.headerActions || []),
        ].find(a => a.id === actionId);

        return action ? action.icon || action.svgIcon || '' : '';
    }

    // Helper method to get all sites for the current user role
    private getAllSitesForRole(): LoginOrganizationSite[] {
        const session = this.sessionService.getSession();
        if (!session?.organizationSites) {
            return [];
        }

        switch (this.userRole()) {
            case 'consignor':
                return session.organizationSites.filter(
                    site =>
                        site.consignor &&
                        (site.roles.includes(UserRoles.Consignor) ||
                            site.roles.includes(UserRoles.RemoteConsignor))
                );
            case 'carrier':
                return session.organizationSites.filter(
                    site =>
                        site.carrier &&
                        (site.roles.includes(UserRoles.Carrier) ||
                            site.roles.includes(UserRoles.RemoteCarrier))
                );
            case 'consignee':
                return session.organizationSites.filter(
                    site =>
                        site.consignee &&
                        (site.roles.includes(UserRoles.Consignee) ||
                            site.roles.includes(UserRoles.RemoteConsignee))
                );
            default:
                return [];
        }
    }

    // Filter button event handlers
    async onLocationFilterClick(): Promise<void> {
        // Get all sites for the current user role
        const allSites = this.getAllSitesForRole();
        const selectedLocationKeys = this.appStore.getSelectedLocationKeys(
            this.userRole()
        );

        // Convert selected keys to site objects
        const selectedSites = allSites.filter(site =>
            selectedLocationKeys.includes(site._key)
        );

        if (!allSites || allSites.length === 0) {
            this.toastService.warning('No sites available for filtering');
            return;
        }

        const currentSiteKey = this.appStore.currentOrganizationSiteKey();

        // Ensure current site is in selected sites if none are selected
        if (selectedSites.length === 0 && currentSiteKey) {
            const currentSite = allSites.find(
                site => site._key === currentSiteKey
            );
            if (currentSite) {
                this.appStore.setSelectedLocations(this.userRole(), [
                    currentSite._key,
                ]);
            }
        }

        // Get updated selection after potential initialization
        const finalSelectedKeys = this.appStore.getSelectedLocationKeys(
            this.userRole()
        );

        // Use the current selection from the store without forcing current site inclusion
        const sitesToPass =
            finalSelectedKeys.length > 0
                ? finalSelectedKeys
                : currentSiteKey
                  ? [currentSiteKey]
                  : [];

        // Open the site filter dialog
        const dialogRef = this.dialog.open(FilterUserSitesComponent, {
            data: {
                sites: allSites,
                currentlySelectedSites: sitesToPass,
            },
            minWidth: '50vw',
            height: '70vh',
            disableClose: true,
        });

        const result = await lastValueFrom(dialogRef.afterClosed());
        if (result) {
            // Save the selected sites to appStore
            const selectedKeys = result.map((site: any) => site._key);
            this.appStore.setSelectedLocations(this.userRole(), selectedKeys);

            // Reload data with new site selection
            this.loadViewData();

            // Emit the action event
            this.actionTriggered.emit({
                actionId: 'filterSites',
                data: result,
            });
        }
    }

    async onColumnFilterClick(): Promise<void> {
        const config = this.currentConfig();
        if (!config) return;

        const viewType = config.type;
        const userRole = config.userRole;

        // Get all available columns from the config
        const allColumns = config.tableConfig.columns.map(col =>
            String(col.key)
        );
        const currentVisibleColumns = this.tablePreferencesStore.hasColumnVisibilitySettings(
            this.sessionService.getSession()._key,
            viewType,
            userRole
        )
            ? this.tablePreferencesStore.getVisibleColumns(
                  this.sessionService.getSession()._key,
                  viewType,
                  userRole
              )
            : allColumns; // Default to all columns if no settings

        // Open the same dialog as used in the original delivery note table
        const dialogRef = this.dialog.open(SelectDisplayedColsDialogComponent, {
            data: {
                allColumns: allColumns,
                displayedColumns: currentVisibleColumns,
                keyLabel:
                    'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DELIVERY-NO',
            },
        });

        const result = await lastValueFrom(dialogRef.afterClosed());
        if (result) {
            const viewKey = `${viewType}_${userRole}`;

            // Handle the column selection result
            if (result.reset) {
                // Reset to show all columns
                this.tablePreferencesStore.clearColumnVisibility(
                    this.sessionService.getSession()._key,
                    viewType,
                    userRole
                );
            } else if (result.displayedColumns) {
                // Apply selected columns
                this.tablePreferencesStore.setColumnVisibility(
                    this.sessionService.getSession()._key,
                    viewKey,
                    result.displayedColumns
                );
            } else if (Array.isArray(result)) {
                // Handle array format
                this.tablePreferencesStore.setColumnVisibility(
                    this.sessionService.getSession()._key,
                    viewKey,
                    result
                );
            }

            // Emit the action event for parent components to handle
            this.actionTriggered.emit({
                actionId: 'showColumnFilter',
                data: result,
            });
        }
    }
}
